import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { idCategorySchema, type IdCategoryInput } from '@pms/shared';
import { getErrorMessage } from '@/lib/errors';
import { idManagementService, type IdCategory } from '@/services/id-management.service';
import { CategoryIcon } from '@/components/id-management/CategoryIcon';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_ICON_BYTES = 2 * 1024 * 1024; // 2 MB

interface Props {
  category?: IdCategory;
  onClose: () => void;
}

export function IdCategoryFormDialog({ category, onClose }: Props) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [iconRemoved, setIconRemoved] = useState(false);
  const [iconError, setIconError] = useState<string | null>(null);

  const { register, control, handleSubmit, formState: { errors } } = useForm<IdCategoryInput>({
    resolver: zodResolver(idCategorySchema),
    defaultValues: { name: category?.name ?? '' },
  });
  const typedName = useWatch({ control, name: 'name' });

  const previewSrc = useMemo(() => (pickedFile ? URL.createObjectURL(pickedFile) : undefined), [pickedFile]);
  useEffect(() => () => {
    if (previewSrc) URL.revokeObjectURL(previewSrc);
  }, [previewSrc]);

  const savedIconPath = iconRemoved ? null : category?.icon_path ?? null;
  const hasIcon = pickedFile !== null || savedIconPath !== null;

  const onFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again still fires a change event.
    e.target.value = '';
    if (!file) return;
    if (!ICON_TYPES.includes(file.type)) {
      setIconError('The icon must be a PNG, JPG or WebP image');
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      setIconError('The icon must be 2 MB or smaller');
      return;
    }
    setIconError(null);
    setPickedFile(file);
  };

  const removeIcon = () => {
    setPickedFile(null);
    setIconRemoved(true);
    setIconError(null);
  };

  const mutation = useMutation({
    mutationFn: async (values: IdCategoryInput): Promise<{ iconFailure: string | null }> => {
      let id: string;
      if (category) {
        await idManagementService.updateCategory(category.id, values);
        id = category.id;
      } else {
        id = (await idManagementService.createCategory(values)).id;
      }
      // The category is saved at this point, so an icon failure mustn't read as a failed save.
      try {
        if (pickedFile) await idManagementService.uploadCategoryIcon(id, pickedFile);
        else if (iconRemoved && category?.icon_path) await idManagementService.removeCategoryIcon(id);
        return { iconFailure: null };
      } catch (e) {
        return { iconFailure: getErrorMessage(e) };
      }
    },
    onSuccess: ({ iconFailure }) => {
      if (iconFailure) toast.error(`The category was saved, but its icon wasn't: ${iconFailure}`);
      else toast.success(category ? 'Category updated' : 'Category created');
      void queryClient.invalidateQueries({ queryKey: ['id-management'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit ID Category' : 'New ID Category'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Icon</Label>
            <div className="flex items-center gap-3">
              <CategoryIcon
                name={typedName || '?'}
                iconPath={savedIconPath}
                previewSrc={previewSrc}
                className="h-16 w-16 rounded-lg"
                letterClassName="text-2xl"
              />
              <div className="min-w-0 space-y-1.5">
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ICON_TYPES.join(',')}
                    className="hidden"
                    aria-label="Icon file"
                    onChange={onFilePicked}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    {hasIcon ? 'Change icon' : 'Upload icon'}
                  </Button>
                  {hasIcon && (
                    <Button type="button" variant="ghost" size="sm" onClick={removeIcon}>
                      Remove
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  PNG, JPG or WebP, up to 2 MB. Square images look best.
                </p>
              </div>
            </div>
            {iconError && <p className="text-xs text-destructive">{iconError}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="id-category-name">Category Name *</Label>
            <Input id="id-category-name" autoFocus placeholder="e.g. Google" maxLength={100} {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {category ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
